import React from 'react';

import { Stack, Typography } from '@gogame/components';
import { ScreenLink, useScreenState } from '@gogame/screens';

import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import CreativeAttributes from 'scenes/Gallery/GalleryPage/Creatives/components/CreativeAttributes';
import CreativeTargeting from 'scenes/Gallery/GalleryPage/Creatives/components/CreativeTargeting';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  AttributeList,
  AttributeItem,
} from 'scenes/Gallery/components/AttributeList';
import {
  EXTENSION,
  HEIGHT,
  ORIENTATION,
  SIZE,
  VIDEO_LENGTH,
  WIDTH,
  RATIO,
} from 'scenes/Gallery/components/File/constants';
import { CONCEPT_ID } from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { conceptPreviewLink } from 'scenes/Gallery/GalleryPage/navigation';

const DetailsColumn = ({ row, showConceptLink }) => {
  const screenState = useScreenState();

  const {
    isAllowSharedWith,
    isAllowViewConcept,
    isAllowExclusiveForAffiliates,
  } = useGalleryPermissions();

  const {
    [CREATIVE.NAME]: name,
    [CREATIVE.SHORT_NAME]: shortName,
    [CREATIVE.CONCEPT]: { [CONCEPT.ID]: conceptId },
    [CREATIVE.IS_SHARED]: isShared,
    [CREATIVE.IS_ORIGINAL]: isOriginal,
    [CREATIVE.CREATIVE_TYPE]: creativeTypeId,
    [CREATIVE.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
    [CREATIVE.EXPIRATION_DATE]: expirationDate,
    [CREATIVE.AI_GENERATED]: aiGenerated,
    [CREATIVE.FILE]: {
      [EXTENSION]: fileExtension,
      [WIDTH]: fileWidth,
      [HEIGHT]: fileHeight,
      [VIDEO_LENGTH]: fileVideoLength,
      [SIZE]: fileSize,
      [ORIENTATION]: fileOrientation,
      [RATIO]: fileRatio,
    },
  } = row;

  const fullName = `${name} / ${shortName}`;

  return (
    <Stack spacing={0.5} zeroMinWidth={true}>
      <Typography variant={'body2'} noWrap={true}>
        {showConceptLink && isAllowViewConcept(row) ? (
          <ScreenLink
            data-cy={'Concept Page'}
            link={conceptPreviewLink}
            pathParams={{
              [CONCEPT_ID]: conceptId,
            }}
            searchParams={{
              filters: screenState.filters,
              creatives: {
                tableSettings: {
                  orderDir: 'asc',
                },
              },
            }}
            underline={'always'}
          >
            {fullName}
          </ScreenLink>
        ) : (
          <span>{fullName}</span>
        )}
      </Typography>

      <Typography variant={'caption'} noWrap={true} component={'div'}>
        <CreativeTargeting data={row} />
      </Typography>

      <Stack spacing={1} direction={'row'} alignItems={'center'}>
        <CreativeAttributes
          isOriginal={isOriginal}
          creativeType={creativeTypeId}
          fileExtension={fileExtension}
          fileVideoLength={fileVideoLength}
          fileWidth={fileWidth}
          fileHeight={fileHeight}
          fileSize={fileSize}
          fileOrientation={fileOrientation}
          fileRatio={fileRatio}
        />
        <AttributeList type={'icon'}>
          {exclusiveForAffiliates.length && isAllowExclusiveForAffiliates ? (
            <AttributeItem valueParser={'exclusiveForAffiliates'} />
          ) : null}
          {aiGenerated ? <AttributeItem valueParser={'aiGenerated'} /> : null}
          {expirationDate ? (
            <AttributeItem
              value={expirationDate}
              valueParser={'expirationDate'}
            />
          ) : null}
          {isShared && isAllowSharedWith ? (
            <AttributeItem valueParser={'shared'} />
          ) : null}
        </AttributeList>
      </Stack>
    </Stack>
  );
};

export default DetailsColumn;
